import React from "react";
import { motion } from "framer-motion";
import {
  BIO, BIO_LONG, STATS, INTL_EXPERIENCE, PORTRAIT, CONTACT, COLLABORATORS,
} from "../../data/content";
import { Reveal, Kicker, RevealHeading } from "./primitives";

export const Studio = () => (
  <section id="studio" className="relative py-14 md:py-40 px-4 md:px-10" data-testid="studio">
    <div className="mx-auto max-w-[1600px]">
      <div className="mb-10 md:mb-24">
        <Kicker index="03">The Founder · Chethan D&apos;Souza</Kicker>
        <RevealHeading text="22+ Years" className="display-h2 mt-3 md:mt-5" />
        <RevealHeading text="Of Impact" className="display-h2 text-muted-foreground" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
        {/* Portrait */}
        <Reveal className="lg:col-span-5" y={60}>
          <div className="relative aspect-[3/4] overflow-hidden border border-border bg-[#0a0a0a]">
            <img
              src={PORTRAIT}
              alt="Chethan D'Souza"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover object-[50%_18%] opacity-90"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
            <div className="absolute bottom-0 left-0 p-5 md:p-6">
              <p className="font-display uppercase text-xl sm:text-2xl leading-none text-white">Chethan D&apos;Souza</p>
              <p className="kicker mt-1.5 md:mt-2 text-[10px] md:text-xs">Founder · Lead Action Designer</p>
            </div>
          </div>
        </Reveal>

        {/* Bio + stats */}
        <div className="lg:col-span-7 flex flex-col justify-between">
          <Reveal>
            <p className="text-sm sm:text-base md:text-lg leading-relaxed text-foreground/90">{BIO}</p>
            <p className="mt-4 sm:mt-6 text-xs sm:text-sm md:text-base leading-relaxed text-muted-foreground">{BIO_LONG}</p>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px mt-8 md:mt-12 border border-border bg-border">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.06}>
                <div className="bg-background p-4 sm:p-5 md:p-6 h-full">
                  <div className="font-display text-3xl sm:text-4xl md:text-5xl leading-none text-[hsl(var(--accent))]">
                    {s.value}
                  </div>
                  <div className="kicker mt-2 sm:mt-3 text-[9px] md:text-xs">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <a
              href={CONTACT.imdb}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 mt-6 sm:mt-8 font-mono-c text-xs tracking-[0.15em] uppercase link-underline"
              data-testid="studio-imdb-link"
            >
              View IMDb Profile ↗
            </a>
          </Reveal>
        </div>
      </div>

      {/* International experience */}
      <div className="mt-16 md:mt-32 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
        <div className="lg:col-span-4">
          <Kicker index="04">International Shoots</Kicker>
          <h3 className="font-display uppercase text-2xl sm:text-3xl md:text-4xl mt-3 md:mt-4 leading-none">
            Across Every Terrain
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground mt-3 md:mt-4 leading-relaxed">
            Extensive experience across desert dune chases, city chases and complex terrains,
            collaborating with international stunt and technical teams as well as skilled local talent.
          </p>
        </div>
        <div className="lg:col-span-8">
          {INTL_EXPERIENCE.map((e, i) => (
            <Reveal key={e.place} delay={i * 0.05}>
              <motion.div
                whileHover={{ x: 8 }}
                transition={{ duration: 0.3 }}
                className="group flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 sm:gap-4 py-4 sm:py-6 border-b border-border"
                data-testid={`intl-${i}`}
              >
                <div className="flex items-baseline gap-3 sm:gap-5">
                  <span className="font-mono-c text-xs text-[hsl(var(--accent))]">
                    0{i + 1}
                  </span>
                  <span className="font-display uppercase text-2xl sm:text-3xl md:text-5xl leading-none group-hover:text-[hsl(var(--accent))] transition-colors">
                    {e.place}
                  </span>
                </div>
                <div className="sm:text-right pl-7 sm:pl-0">
                  <div className="text-xs sm:text-sm text-foreground font-medium">{e.project}</div>
                  <div className="kicker text-[9px] sm:text-xs mt-0.5 sm:mt-0">{e.detail}</div>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Closely Associated With */}
      <div className="mt-16 md:mt-32">
        <Kicker index="05" className="mb-6 md:mb-10">Closely Associated With</Kicker>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px border border-border bg-border">
          {COLLABORATORS.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.08}>
              <div className="bg-background p-5 sm:p-8 h-full">
                <div className="font-display uppercase text-xl sm:text-2xl md:text-3xl leading-none">{c.name}</div>
                <p className="text-xs sm:text-sm text-muted-foreground mt-2 sm:mt-3 leading-relaxed">{c.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);
